// รายงานการเข้าเรียน (ภาคศาสนา) — หัวหน้าวิชาการศาสนา (pos 57)
//
// รวม "วันนี้" กับ "ย้อนหลัง" ไว้หน้าเดียว ต่างจากภาคสามัญที่แยกเป็นสองหน้า
// (academic/general-attendance-report + academic/attendance-history-report)
// เพราะคำถามเป็นคำถามเดียวกัน ต่างแค่ช่วงเวลา — แยกสองหน้าแล้วต้องตามแก้คู่กันตลอด
//
// ลำดับบนหน้าจอเรียงตาม "สิ่งที่ต้องลงมือก่อน": คาบที่ยังไม่เช็คชื่อมาก่อนสถิติ
// เพราะสถิติจากคาบที่ยังไม่เช็คคือสถิติที่ยังเชื่อไม่ได้
import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Card, Col, Form, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  API, ATT_STATUS, AcademicFilterBar, DateRangeBar, StatTile, StatusBar, StatusDot, StudentAvatar,
  alertRequestError, roomLabel, thaiDate, useAcademicFilters, useDarkTables, useDateRange,
  useStudyYears,
} from './academicShared'

const EMPTY = {
  range: { start: '', end: '', days: 0 },
  summary: { present: 0, absent: 0, late: 0, leave: 0, total: 0, absenceRate: 0, expectedSlots: 0, checkedSlots: 0 },
  byDate: [], byRoom: [], unchecked: [], noTeaching: [], absentStudents: [],
}

// แถบวัดความคืบหน้าการเช็คชื่อ — รางเป็นสีเดียวกันแบบจาง เพื่อให้อ่านสถานะได้ทั้งแถบ
const CoverageMeter = ({ done, total }) => {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  const color = pct >= 90 ? '#198754' : pct >= 70 ? '#ffc107' : '#dc3545'
  return (
    <div className="rounded-pill mt-2" style={{ height: 6, background: `${color}29` }}>
      <div className="rounded-pill h-100" style={{ width: `${pct}%`, background: color }} />
    </div>
  )
}

const Legend = ({ t }) => (
  <div className="d-flex flex-wrap gap-3">
    {ATT_STATUS.map(({ key, color }) => (
      <span key={key} className="d-inline-flex align-items-center gap-1 fs-xs text-secondary">
        <StatusDot color={color} size={8} />
        {t.academic.attendance[key]}
      </span>
    ))}
  </div>
)

export default function IslamicAttendanceReport() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  const navigate = useNavigate()
  useDarkTables()

  const { years, yearId, setYearId } = useStudyYears()
  const filters = useAcademicFilters(yearId, t)
  const range = useDateRange('day')
  const [data, setData] = useState(EMPTY)
  const [showAllRooms, setShowAllRooms] = useState(false)

  useEffect(() => {
    if (!yearId) return
    let alive = true
    setLoading(true)
    const q = new URLSearchParams(filters.query.replace(/^\?/, ''))
    q.set('start', range.start)
    q.set('end', range.end)
    axiosInstance.get(`${API}/islamic/academic/attendance/${yearId}?${q}`)
      .then(res => { if (alive) setData(res.data || EMPTY) })
      .catch(err => { if (alive) { setData(EMPTY); alertRequestError(t, err, t.common.error) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [yearId, filters.query, range.start, range.end, setLoading, t])

  const { summary, byDate, byRoom, unchecked, noTeaching, absentStudents } = data
  const multiDay = (data.range?.days || 0) > 1

  // ตารางรายห้องเรียงห้องที่ขาดมากขึ้นก่อน แต่ค่าเริ่มต้นโชว์เฉพาะห้องที่มีเรื่องต้องดู
  // (มีคนขาด หรือเช็คชื่อไม่ครบ) — ทั้งโรงเรียนมีหลายสิบห้อง ส่วนใหญ่ปกติ
  const rankedRooms = useMemo(() => {
    const list = [...byRoom].sort((a, b) =>
      b.absenceRate - a.absenceRate
      || (b.expected_slots - b.checked_slots) - (a.expected_slots - a.checked_slots))
    if (showAllRooms) return list
    return list.filter(r => r.absent > 0 || r.checked_slots < r.expected_slots)
  }, [byRoom, showAllRooms])

  const hiddenRooms = byRoom.length - rankedRooms.length

  return (
    <React.Fragment>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item">{t.academic.groupTitle}</li>
            <li className="breadcrumb-item active">{t.academic.attendance.pageTitle}</li>
          </ol>
          <h4 className="main-title mb-0">{t.academic.attendance.pageTitle}</h4>
        </div>
      </div>

      <Card className="card-one mb-3">
        <Card.Body>
          <DateRangeBar t={t} range={range} />
          <hr className="my-3" />
          <AcademicFilterBar t={t} years={years} yearId={yearId} setYearId={setYearId} filters={filters} />
        </Card.Body>
      </Card>

      {/* ตัวเลขรวมของช่วงที่เลือก */}
      <div className="row g-2 mb-3">
        <Col xs={12} md={4} lg>
          <Card className="card-one h-100">
            <Card.Body className="p-3">
              <div className="fs-xs text-secondary">{t.academic.attendance.coverage}</div>
              <div className="fs-20 fw-semibold lh-1 mt-2">
                {summary.checkedSlots}
                <span className="fs-sm fw-normal text-secondary"> / {summary.expectedSlots} {t.academic.attendance.slots}</span>
              </div>
              <CoverageMeter done={summary.checkedSlots} total={summary.expectedSlots} />
            </Card.Body>
          </Card>
        </Col>
        {ATT_STATUS.map(({ key, color }) => (
          <Col xs={6} md={2} lg key={key}>
            <StatTile
              color={color}
              label={t.academic.attendance[key]}
              value={summary[key].toLocaleString('th-TH')}
              sub={key === 'absent' && summary.total > 0 ? t.academic.attendance.ofAll(summary.absenceRate) : null}
            />
          </Col>
        ))}
      </div>

      {/* คาบที่ยังไม่เช็คชื่อ — ขึ้นก่อนสถิติเพราะเป็นสิ่งที่ต้องตามให้เสร็จก่อน */}
      <Card className="card-one mb-3">
        <Card.Header className="d-flex flex-wrap align-items-center gap-2">
          <Card.Title as="h6" className="mb-0">{t.academic.attendance.uncheckedTitle}</Card.Title>
          <Badge bg={unchecked.length ? 'danger' : 'success'} className="fs-xs">
            {t.academic.attendance.slotCount(unchecked.length)}
          </Badge>
          {noTeaching.length > 0 && (
            <span className="fs-xs text-secondary">
              {t.academic.attendance.excusedNote(noTeaching.length)}
            </span>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {unchecked.length === 0 ? (
            <div className="text-center text-secondary py-4 fs-sm">{t.academic.attendance.allChecked}</div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: 420 }}>
              <Table className="table-sm mb-0 align-middle">
                <thead>
                  <tr>
                    {multiDay && <th style={{ width: 130 }}>{t.academic.attendance.colDate}</th>}
                    <th style={{ width: 70 }}>{t.academic.attendance.colPeriod}</th>
                    <th style={{ width: 110 }}>{t.academic.attendance.colRoom}</th>
                    <th>{t.academic.attendance.colSubject}</th>
                    <th style={{ width: 180 }}>{t.academic.attendance.colTeacher}</th>
                  </tr>
                </thead>
                <tbody>
                  {unchecked.map(u => (
                    <tr key={`${u.date}-${u.period_id}-${u.is_teachId}`}>
                      {multiDay && <td className="fs-sm">{thaiDate(u.date)}</td>}
                      <td className="fs-sm">{u.period_order}</td>
                      <td className="fs-sm">{roomLabel(u)}</td>
                      <td className="fs-sm">{u.subject || <span className="text-secondary">-</span>}</td>
                      <td className="fs-sm">
                        {u.teacher || <span className="text-danger">{t.academic.attendance.noTeacher}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* สัดส่วนรายวัน — เฉพาะเมื่อเลือกเป็นช่วง วันเดียวดูจากการ์ดด้านบนพอแล้ว */}
      {multiDay && (
        <Card className="card-one mb-3">
          <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <Card.Title as="h6" className="mb-0">{t.academic.attendance.byDateTitle}</Card.Title>
            <Legend t={t} />
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="table-sm mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>{t.academic.attendance.colDate}</th>
                    <th style={{ minWidth: 160 }}>{t.academic.attendance.colShare}</th>
                    {ATT_STATUS.map(s => (
                      <th key={s.key} className="text-end" style={{ width: 78 }}>{t.academic.attendance[s.key]}</th>
                    ))}
                    <th className="text-end" style={{ width: 78 }}>{t.academic.attendance.colAbsentRate}</th>
                  </tr>
                </thead>
                <tbody>
                  {byDate.map(d => {
                    const rate = d.total > 0 ? +((d.absent / d.total) * 100).toFixed(1) : 0
                    return (
                      <tr key={d.date}>
                        <td className="fs-sm">{thaiDate(d.date)}</td>
                        <td>
                          {d.total > 0
                            ? <StatusBar t={t} counts={d} total={d.total} />
                            : <span className="fs-xs text-secondary">{t.academic.attendance.noRecord}</span>}
                        </td>
                        {ATT_STATUS.map(s => (
                          <td key={s.key} className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {d[s.key] || <span className="text-secondary">-</span>}
                          </td>
                        ))}
                        <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {d.total > 0 ? `${rate}%` : <span className="text-secondary">-</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* รายห้อง */}
      <Card className="card-one mb-3">
        <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-flex align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">{t.academic.attendance.byRoomTitle}</Card.Title>
            <Badge bg="secondary" className="fs-xs">{t.academic.attendance.roomCount(rankedRooms.length)}</Badge>
          </div>
          <div className="d-flex align-items-center gap-3">
            <Legend t={t} />
            {(hiddenRooms > 0 || showAllRooms) && (
              <Form.Check
                type="switch"
                className="fs-xs mb-0"
                checked={showAllRooms}
                onChange={e => setShowAllRooms(e.target.checked)}
                label={t.academic.attendance.showAllRooms(hiddenRooms)}
              />
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {rankedRooms.length === 0 ? (
            <div className="text-center text-secondary py-4 fs-sm">{t.academic.attendance.noRoomIssue}</div>
          ) : (
            <div className="table-responsive">
              <Table className="table-sm mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>{t.academic.attendance.colRoom}</th>
                    <th style={{ width: 110 }}>{t.academic.attendance.colChecked}</th>
                    <th style={{ minWidth: 160 }}>{t.academic.attendance.colShare}</th>
                    {ATT_STATUS.map(s => (
                      <th key={s.key} className="text-end" style={{ width: 78 }}>{t.academic.attendance[s.key]}</th>
                    ))}
                    <th className="text-end" style={{ width: 78 }}>{t.academic.attendance.colAbsentRate}</th>
                  </tr>
                </thead>
                <tbody>
                  {rankedRooms.map(r => (
                    <tr key={r.ir_id}>
                      <td className="fs-sm">{roomLabel(r)}</td>
                      <td className="fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        <span className={r.checked_slots < r.expected_slots ? 'text-danger fw-semibold' : undefined}>
                          {r.checked_slots}
                        </span>
                        <span className="text-secondary"> / {r.expected_slots}</span>
                      </td>
                      <td>
                        {r.total > 0
                          ? <StatusBar t={t} counts={r} total={r.total} />
                          : <span className="fs-xs text-secondary">{t.academic.attendance.noRecord}</span>}
                      </td>
                      {ATT_STATUS.map(s => (
                        <td key={s.key} className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {r[s.key] || <span className="text-secondary">-</span>}
                        </td>
                      ))}
                      <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {r.total > 0 ? `${r.absenceRate}%` : <span className="text-secondary">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* นักเรียนที่ขาดในช่วงนี้ */}
      <Card className="card-one mb-3">
        <Card.Header className="d-flex flex-wrap align-items-center gap-2">
          <Card.Title as="h6" className="mb-0">{t.academic.attendance.absentStudentsTitle}</Card.Title>
          <Badge bg="secondary" className="fs-xs">{t.common.personCount(absentStudents.length)}</Badge>
          {absentStudents.length >= 100 && (
            <span className="fs-xs text-secondary">{t.academic.attendance.cappedNote}</span>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {absentStudents.length === 0 ? (
            <div className="text-center text-secondary py-4 fs-sm">{t.academic.attendance.noAbsentStudent}</div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: 480 }}>
              <Table className="table-sm mb-0 align-middle table-hover">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>{t.academic.attendance.colStudent}</th>
                    <th style={{ width: 110 }}>{t.academic.attendance.colRoom}</th>
                    <th className="text-end" style={{ width: 78 }}>{t.academic.attendance.absent}</th>
                    <th className="text-end" style={{ width: 78 }}>{t.academic.attendance.late}</th>
                    <th className="text-end" style={{ width: 78 }}>{t.academic.attendance.leave}</th>
                    <th className="text-end" style={{ width: 90 }}>{t.academic.attendance.colOfChecked}</th>
                  </tr>
                </thead>
                <tbody>
                  {absentStudents.map((s, i) => (
                    <tr
                      key={`${s.st_id}-${s.ir_id}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/islamic/student-tracking/${s.st_id}`)}
                    >
                      <td className="text-secondary fs-xs">{i + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={s.st_photo} name={studentDisplayName(s, lang)} size={28} />
                          <div style={{ minWidth: 0 }}>
                            <div className="fs-sm text-truncate"><StudentName student={s} lang={lang} /></div>
                            <div className="fs-xs text-secondary">{s.st_idnumber}</div>
                          </div>
                        </div>
                      </td>
                      <td className="fs-sm">{roomLabel(s)}</td>
                      <td className="text-end fs-sm fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.absent}</td>
                      <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.late || <span className="text-secondary">-</span>}</td>
                      <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{s.leave || <span className="text-secondary">-</span>}</td>
                      <td className="text-end fs-sm text-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {s.total > 0 ? `${Math.round((s.absent / s.total) * 100)}%` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        {absentStudents.length > 0 && (
          <Card.Footer className="fs-xs text-secondary">
            {t.academic.attendance.clickStudentHint}
          </Card.Footer>
        )}
      </Card>

      {/* วันที่ปฏิทินสั่งงดสอน — เก็บไว้ท้ายสุดเป็นข้อมูลประกอบ ไม่ใช่สิ่งที่ต้องลงมือ */}
      {noTeaching.length > 0 && (
        <Card className="card-one mb-3">
          <Card.Header>
            <Card.Title as="h6" className="mb-0">{t.academic.attendance.noTeachingTitle}</Card.Title>
          </Card.Header>
          <Card.Body className="fs-sm">
            {[...new Set(noTeaching.map(n => `${n.date}|${n.title}`))].map(k => {
              const [date, title] = k.split('|')
              return (
                <div key={k} className="d-flex gap-2">
                  <span className="text-secondary" style={{ minWidth: 120 }}>{thaiDate(date)}</span>
                  <span>{title}</span>
                </div>
              )
            })}
            <div className="fs-xs text-secondary mt-2">{t.academic.attendance.noTeachingNote}</div>
          </Card.Body>
        </Card>
      )}
    </React.Fragment>
  )
}
